import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { delay, Observable, retryWhen, scan } from 'rxjs';
import { HttpStatusCode } from '../types/http-code.type';

export const RETRY_REQUEST_CONFIG = new InjectionToken<RetryRequestConfig>('RETRY_REQUEST_CONFIG');

export const SKIP_RETRY_INTERCEPTOR = 'skip-retry-interceptor' as const;

export type RetryRequestConfig = {
  maxRetries: number;
  delayMs: number;
  codeExclude?: HttpStatusCode[];
};

@Injectable()
export class RetryRequestInterceptor implements HttpInterceptor {
  private readonly config = inject(RETRY_REQUEST_CONFIG, { optional: true }) || { maxRetries: 5, delayMs: 3000 };

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (error instanceof HttpErrorResponse) {
              // Check if the error status is in the excluded list
              const statusCodeExclude = this.config.codeExclude ?? [];

              if (req.headers.get(SKIP_RETRY_INTERCEPTOR) === 'true') {
                console.warn(`Skipping retry by ${SKIP_RETRY_INTERCEPTOR}, ${error.status}: ${error.message}`);
                throw error;
              }

              if (statusCodeExclude && statusCodeExclude.find(code => Number(code) === error.status)) {
                console.warn(`Skipping retry for status ${error.status}: ${error.message}`);
                throw error; // Stop retrying
              }
            }

            if (retryCount >= this.config.maxRetries) {
              throw error; // Stop retrying after max retries
            }

            console.warn(`Retry attempt ${retryCount + 1} after ${this.config.delayMs / 1000} seconds...`);
            return retryCount + 1;
          }, 0),
          delay(this.config.delayMs) // Apply retry delay
        )
      )
    );
  }
}
